import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import { ensureDefined } from "common/utils/assertOfType";
import { validateIsExactText } from "common/utils/formValidators";
import BuilderModal from "lib/components/builders/communities/plans/modals/BuilderModal";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import { detachHomePlanChain } from "lib/storage/homePlanStorage";
import type { FormEvent } from "react";
import { useState } from "react";
import Button from "ui/buttons/Button";
import InputGroupText from "ui/forms/InputGroupText";
import useAppContext from "ui/hooks/useAppContext";
import { Message } from "ui/text/Message";
import styles from "./HomePlanActionConfirmationModal.module.scss";

interface DetachHomePlanModalProps {
  homePlan: ClientHomePlan;
  builderId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: (hp: ClientHomePlanHydratedNoDoc) => void;
}

export default function DetachHomePlanModal({
  open,
  setOpen,
  onSuccess,
  homePlan,
  builderId,
}: DetachHomePlanModalProps) {
  const { setUserNotification } = useAppContext();
  const [confirmText, setConfirmText] = useState<string>();
  const isDisabled = () => validateIsExactText(confirmText, homePlan?.planName ?? undefined, true);
  const [isDetaching, setIsDetaching] = useState(false);
  const sendHomePlanEvent = useHomePlanEvents(homePlan);

  const handleClose = () => {
    setConfirmText("");
    setOpen(false);
  };

  const detachHomePlan = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsDetaching(true);
    const [updatedHomePlan, err] = await detachHomePlanChain(builderId, homePlan?.id);
    if (err) {
      setUserNotification(err);
      setIsDetaching(false);
      handleClose();
    } else {
      if (!err) {
        sendHomePlanEvent("config:detached_homeplan");

        setUserNotification({
          messageType: "success",
          message: `${updatedHomePlan?.planName || "Home Plan"} detached.`,
        });
        if (onSuccess) {
          onSuccess(ensureDefined(updatedHomePlan));
        }
        setIsDetaching(false);
        handleClose();
      }
    }
  };

  return (
    <BuilderModal
      open={open}
      onClose={handleClose}
      title="Detach Home Plan From Studio file"
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          {" "}
          <Button btnSize="large" type="button" btnStyle="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!confirmText || !!isDisabled() || isDetaching}
            type="submit"
            btnSize="large"
            btnStyle="primary"
            form="detach-home-plan-modal"
          >
            {isDetaching ? "Detaching..." : "Detach Home Plan"}
          </Button>
        </>
      }
    >
      <form onSubmit={detachHomePlan} id="detach-home-plan-modal">
        <Message messageType="warning" variant="inverted" marginBottom>
          Warning: this action is irreversible.
        </Message>
        <div className={styles.modalBody}>
          <p className={styles.modalBodyText}>
            Detaching this Home Plan will generate a corresponding lot-specific Studio file that can be edited. Any
            edits to this Studio file will only affect this detached Home Plan.
          </p>
          <p className={styles.modalBodyText}>
            To detach this Home Plan from its original Studio file, type <b>{homePlan?.planName}</b> in the field below:
          </p>
          <div className={styles.confirmationInputWrapper}>
            <InputGroupText
              type="text"
              labelText="Confirm"
              inputValue={confirmText}
              onChange={(value) => setConfirmText(value)}
              id="detach-confirmation"
              placeholder={homePlan?.planName}
              validate={isDisabled}
            />
          </div>
        </div>
      </form>
    </BuilderModal>
  );
}
